/**
 * /reports/library — biblioteca de relatórios gerados.
 *
 * Tabela com filtros, ações (download, rename, delete), polling automático
 * pra reports em status pending/running.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { reportsApi } from "@/api/reports";
import { apiErrorMessage } from "@/api/client";
import { qk, useReports, useTenants } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { ReportFormat, ReportListItem, ReportStatus, ReportType } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input, Select } from "@/components/Field";
import { Badge } from "@/components/Badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Skeleton } from "@/components/Skeleton";
import { formatDate } from "@/lib/format";

const TYPE_OPTIONS: { value: ReportType | ""; label: string }[] = [
  { value: "", label: "Todos os tipos" },
  { value: "completo",    label: "Completo" },
  { value: "temperatura", label: "Temperatura" },
  { value: "eletrico",    label: "Elétrico" },
  { value: "executivo",   label: "Executivo" },
];

const STATUS_OPTIONS: { value: ReportStatus | ""; label: string }[] = [
  { value: "", label: "Todos os status" },
  { value: "pending", label: "Pendente" },
  { value: "running", label: "Gerando" },
  { value: "done",    label: "Pronto" },
  { value: "failed",  label: "Falhou" },
];

const STATUS_TONE: Record<ReportStatus, "default" | "warning" | "success" | "danger"> = {
  pending: "default",
  running: "warning",
  done:    "success",
  failed:  "danger",
};

const STATUS_LABEL: Record<ReportStatus, string> = {
  pending: "Pendente",
  running: "Gerando…",
  done:    "Pronto",
  failed:  "Falhou",
};

export function ReportsLibraryPage() {
  const auth = useAuth();
  // Mesmos papéis das rotas (api/app/routers/reports.py): gerar e renomear
  // é de operador pra cima; apagar é só admin — o PDF leva código de
  // verificação, e apagar faz /validar negar um documento legítimo.
  const podeGerar = auth.hasMinRole("operator");
  const podeApagar = auth.hasMinRole("admin");
  const tenants = useTenants(auth.isSuperAdmin);
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");

  const [search, setSearch] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<ReportType | "">("");
  const [statusFilter, setStatusFilter] = useState<ReportStatus | "">("");

  // Detecta se há reports em andamento → liga polling automático
  const filters = useMemo(() => ({
    ...(search && { search }),
    ...(tenantFilter && { tenant_id: tenantFilter }),
    ...(typeFilter && { report_type: typeFilter }),
    ...(statusFilter && { status: statusFilter }),
    limit: 100,
  }), [search, tenantFilter, typeFilter, statusFilter]);

  const list = useReports(filters);

  // Auto-refresh quando há jobs em andamento
  useEffect(() => {
    const items = list.data ?? [];
    const hasRunning = items.some((r) => r.status === "pending" || r.status === "running");
    if (!hasRunning) return;
    const t = setInterval(() => {
      void qc.invalidateQueries({ queryKey: ["reports"] });
    }, 2500);
    return () => clearInterval(t);
  }, [list.data, qc]);

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      reportsApi.rename(id, name),
    onSuccess: () => {
      toast.success("Renomeado");
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => reportsApi.remove(id),
    onSuccess: () => {
      toast.success("Excluído");
      setDeleteTarget(null);
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const [deleteTarget, setDeleteTarget] = useState<ReportListItem | null>(null);

  async function handleDownload(item: ReportListItem, fmt: ReportFormat) {
    try {
      const blob = await reportsApi.download(item.id, fmt);
      const ext = fmt === "pdf" ? "pdf" : "zip";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.name}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  function handleRename(item: ReportListItem) {
    const name = prompt("Novo nome:", item.name);
    if (name && name.trim() && name !== item.name) {
      renameMut.mutate({ id: item.id, name: name.trim() });
    }
  }

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Biblioteca de Relatórios"
        description="Histórico de relatórios PDF e CSV gerados — retenção mínima 2 anos."
        actions={podeGerar && (
          <Link to="/reports/generate">
            <Button icon={<Plus className="h-4 w-4" />}>Novo relatório</Button>
          </Link>
        )}
      />

      {/* Filtros */}
      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Buscar nome">
              <div className="relative">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ex: Relatorio_LAIT_..."
                />
                <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
              </div>
            </Field>
            {auth.isSuperAdmin && (
              <Field label="Tenant">
                <Select
                  value={tenantFilter}
                  onChange={(e) => setTenantFilter(e.target.value)}
                  options={[
                    { value: "", label: "Todos" },
                    ...(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name })),
                  ]}
                />
              </Field>
            )}
            <Field label="Tipo">
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as ReportType | "")}
                options={TYPE_OPTIONS}
              />
            </Field>
            <Field label="Status">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ReportStatus | "")}
                options={STATUS_OPTIONS}
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader title={`${list.data?.length ?? 0} relatório(s)`} />
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-4">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (list.data ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum relatório encontrado.
              {podeGerar && (
                <> <Link to="/reports/generate" className="text-brand-600 underline">Gerar o primeiro</Link>.</>
              )}
            </p>
          ) : (
            <ul className="divide-y divide-surface-100 dark:divide-slate-800">
              {(list.data ?? []).map((item) => (
                <ReportRow
                  key={item.id}
                  item={item}
                  highlighted={focusId === item.id}
                  onDownload={(fmt) => handleDownload(item, fmt)}
                  onRename={podeGerar ? () => handleRename(item) : undefined}
                  onDelete={podeApagar ? () => setDeleteTarget(item) : undefined}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Excluir relatório?"
        message={
          deleteTarget
            ? `"${deleteTarget.name}" será removido permanentemente, junto com PDF e CSV. Ação irreversível.`
            : ""
        }
        confirmLabel="Excluir"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- Row ----------

function ReportRow({
  item,
  highlighted,
  onDownload,
  onRename,
  onDelete,
}: {
  item: ReportListItem;
  highlighted: boolean;
  onDownload: (fmt: ReportFormat) => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  const isProcessing = item.status === "pending" || item.status === "running";
  return (
    <li
      className={clsx(
        "flex items-start justify-between gap-4 px-4 py-3 transition",
        highlighted && "bg-brand-50/40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-slate-900 dark:text-slate-100">{item.name}</span>
          <Badge tone={STATUS_TONE[item.status]}>
            {isProcessing && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
            {STATUS_LABEL[item.status]}
            {isProcessing && item.progress_pct ? ` (${item.progress_pct}%)` : ""}
          </Badge>
          {item.has_pdf && <Badge tone="brand">PDF</Badge>}
          {item.has_csv && <Badge tone="brand">CSV</Badge>}
        </div>

        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500 dark:text-slate-400">
          <span>{item.report_type}</span>
          {item.period_start && item.period_end && (
            <span>
              {formatDate(item.period_start, "dd/MM/yyyy")} – {formatDate(item.period_end, "dd/MM/yyyy")}
            </span>
          )}
          {item.user_email && <span>por {item.user_email}</span>}
          <span>criado {formatDate(item.created_at)}</span>
          {item.file_size_bytes != null && (
            <span>{formatBytes(item.file_size_bytes)}</span>
          )}
        </div>
      </div>

      <div className="flex flex-none items-center gap-1">
        {item.status === "done" && item.has_pdf && (
          <Button
            size="sm"
            variant="ghost"
            icon={<FileText className="h-4 w-4" />}
            onClick={() => onDownload("pdf")}
          >
            PDF
          </Button>
        )}
        {item.status === "done" && item.has_csv && (
          <Button
            size="sm"
            variant="ghost"
            icon={<FileSpreadsheet className="h-4 w-4" />}
            onClick={() => onDownload("csv")}
          >
            CSV
          </Button>
        )}
        {onRename && (
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={onRename} />
        )}
        {onDelete && (
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={onDelete} />
        )}
      </div>
    </li>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
